/**
 * The footer a data-plane card grows only when it is answering inside the
 * assistant (Figma `LrdQopN1jNWKJFWxOkn0IY` node `360:14497`, "procedência"):
 * three read-only chips naming what the figure is scoped to, and a link back
 * to the screen that owns it. On that screen itself the link would point at
 * the page it is already on and the chips would repeat information the
 * screen's own filters already show, so `SentimentCard` never renders this
 * unless the assistant supplies it (`provenance` prop).
 */

import { Calendar, Check, MapPinned, Users } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { mt } from '../../../chrome/measure/messages';
import { Chip } from '../../../ui/chip';

export interface ProvenanceStripProps {
  /** Whether the figure came from a live tool read, as opposed to the
   *  estimate pipeline `chrome/measure`'s `EstimateLabel` covers elsewhere. */
  measured: boolean;
  /** Already-formatted, same convention as `AssistantScope`'s own labels:
   *  the caller has the locale and the plural rule, this component does not.
   *  Omitted where the caller has no accounts dimension to report. */
  accountsLabel?: string;
  windowLabel: string;
  /** The figure's own screen. */
  to: string;
  /** The link's visible text, e.g. "Abrir em Sentimento". */
  label: string;
}

export function ProvenanceStrip(props: ProvenanceStripProps): ReactNode {
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-3">
      <Chip tone={props.measured ? 'positive' : 'outline'} icon={<Check aria-hidden />}>
        {mt(props.measured ? 'fidelity.MEASURED' : 'fidelity.ESTIMATED')}
      </Chip>
      {props.accountsLabel !== undefined && (
        <Chip icon={<Users aria-hidden />}>{props.accountsLabel}</Chip>
      )}
      <Chip icon={<Calendar aria-hidden />}>{props.windowLabel}</Chip>
      <div className="flex-1" />
      <Link
        to={props.to}
        className="inline-flex items-center gap-1.5 text-label-sm font-medium text-primary-ink hover:underline"
      >
        <MapPinned aria-hidden className="size-3.25" />
        {props.label}
      </Link>
    </div>
  );
}
